import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  DatePicker,
  Drawer,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  useGenerateAdoptionReportMutation,
  useGetAdoptionReportsQuery,
  useLazyGetAdoptionReportQuery,
} from "../../../../store/api/adoptionReportsApi";
import {
  AdoptionLevel,
  AdoptionReportPayload,
  AdoptionReportStatus,
  AdoptionReportVersion,
} from "../../../../services/adoptionReports/adoptionReports.types";
import { getApiErrorMessage } from "../../../../utils";
import { pdfFileName } from "./pdf/format";

const DEFAULT_FROM = "2026-09-01";
const DEFAULT_TO = "2026-10-31";

/** Cada cuánto se consulta el estado mientras el worker genera. */
const POLL_MS = 5000;

const STATUS_TAGS: Record<AdoptionReportStatus, { color: string; label: string }> = {
  PENDING: { color: "default", label: "En cola" },
  IN_PROGRESS: { color: "processing", label: "Generando…" },
  COMPLETED: { color: "green", label: "Lista" },
  FAILED: { color: "red", label: "Falló" },
};

const isActive = (row: AdoptionReportVersion) =>
  row.status === "PENDING" || row.status === "IN_PROGRESS";

const LEVEL_OPTIONS: { value: AdoptionLevel; label: string }[] = [
  { value: "KINDER", label: "Kinder" },
  { value: "PRIMARIA", label: "Primaria" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  schoolId: number;
  schoolName: string;
  showKinder: boolean;
  showPrimaria: boolean;
}

/**
 * Arma el PDF en el navegador con el snapshot guardado. El renderer y el
 * documento se importan recién acá para no sumar su peso a la ficha.
 */
async function downloadPdf(payload: AdoptionReportPayload): Promise<void> {
  const { pdf } = await import("@react-pdf/renderer");
  const { AdoptionReportDocument } = await import(
    "./pdf/AdoptionReportDocument"
  );
  const blob = await pdf(<AdoptionReportDocument payload={payload} />).toBlob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = pdfFileName(payload);
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

/**
 * SCRUM-1557 — reporte de adopción de ESTA escuela. Se genera de a una
 * escuela y nivel: cada «Generar» encola una versión nueva (append-only) que
 * arma el worker de reportes; mientras haya alguna en cola o generándose se
 * consulta el estado. El PDF se arma siempre desde una versión lista.
 */
const AdoptionReportDrawer: React.FC<Props> = ({
  open,
  onClose,
  schoolId,
  schoolName,
  showKinder,
  showPrimaria,
}) => {
  const { message } = App.useApp();
  const levels = useMemo(
    () =>
      LEVEL_OPTIONS.filter((o) =>
        o.value === "KINDER" ? showKinder : showPrimaria,
      ),
    [showKinder, showPrimaria],
  );
  const [level, setLevel] = useState<AdoptionLevel>(
    levels[0]?.value ?? "PRIMARIA",
  );
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs(DEFAULT_FROM),
    dayjs(DEFAULT_TO),
  ]);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [quality, setQuality] = useState<AdoptionReportPayload | null>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!levels.some((o) => o.value === level) && levels[0]) {
      setLevel(levels[0].value);
    }
  }, [levels, level]);

  const versions = useGetAdoptionReportsQuery(
    { schoolId, level },
    {
      skip: !open || levels.length === 0,
      pollingInterval: polling ? POLL_MS : 0,
    },
  );

  useEffect(() => {
    setPolling((versions.data ?? []).some(isActive));
  }, [versions.data]);
  const [generate, generation] = useGenerateAdoptionReportMutation();
  const [fetchReport] = useLazyGetAdoptionReportQuery();

  const handleGenerate = async () => {
    try {
      const result = await generate({
        schoolId,
        level,
        from: range[0].format("YYYY-MM-DD"),
        to: range[1].format("YYYY-MM-DD"),
      }).unwrap();
      message.success(
        `Versión ${result.report_version} en cola: se genera en uno o dos minutos.`,
      );
      versions.refetch();
    } catch (error) {
      message.error(
        getApiErrorMessage(error, "No se pudo generar el reporte."),
      );
    }
  };

  const loadPayload = (id: number) => fetchReport(id, true).unwrap();

  const handleDownload = async (row: AdoptionReportVersion) => {
    setDownloadingId(row.id);
    try {
      await downloadPdf(await loadPayload(row.id));
    } catch (error) {
      message.error(getApiErrorMessage(error, "No se pudo armar el PDF."));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleQuality = async (row: AdoptionReportVersion) => {
    try {
      setQuality(await loadPayload(row.id));
    } catch (error) {
      message.error(
        getApiErrorMessage(error, "No se pudo leer la versión."),
      );
    }
  };

  const columns = [
    {
      title: "Versión",
      dataIndex: "report_version",
      key: "report_version",
      render: (v: number) => `v${v}`,
    },
    {
      title: "Periodo",
      key: "period",
      render: (_: unknown, row: AdoptionReportVersion) =>
        `${dayjs(row.period_start).format("DD/MM")} – ${dayjs(row.period_end).format("DD/MM/YYYY")}`,
    },
    {
      title: "Estado",
      key: "status",
      render: (_: unknown, row: AdoptionReportVersion) => {
        const tag = (
          <Tag color={STATUS_TAGS[row.status].color}>
            {STATUS_TAGS[row.status].label}
          </Tag>
        );
        return row.status === "FAILED" && row.error_message ? (
          <Tooltip title={row.error_message}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: "Pedida",
      key: "requested",
      render: (_: unknown, row: AdoptionReportVersion) => (
        <span>
          {dayjs(row.requested_at).format("DD/MM/YYYY HH:mm")}
          <br />
          <Typography.Text type="secondary">
            {row.generated_by ?? "—"}
          </Typography.Text>
        </span>
      ),
    },
    {
      title: "Avisos",
      key: "warnings",
      render: (_: unknown, row: AdoptionReportVersion) =>
        row.status !== "COMPLETED" ? null : (
        <Space size={4} wrap>
          {row.unavailable_count > 0 && (
            <Tag color="orange">{row.unavailable_count} sin dato</Tag>
          )}
          {row.ai_fallback && <Tag color="gold">texto sin IA</Tag>}
          {row.unavailable_count === 0 && !row.ai_fallback && (
            <Tag color="green">OK</Tag>
          )}
        </Space>
        ),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, row: AdoptionReportVersion) => (
        <Space>
          <Button
            type="primary"
            size="small"
            data-testid={`download-${row.id}`}
            loading={downloadingId === row.id}
            disabled={row.status !== "COMPLETED"}
            onClick={() => handleDownload(row)}
          >
            Descargar PDF
          </Button>
          <Button
            size="small"
            disabled={row.status !== "COMPLETED"}
            onClick={() => handleQuality(row)}
          >
            Ver calidad
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={760}
      title={`Reporte de adopción · ${schoolName}`}
      destroyOnClose
    >
      {levels.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="La escuela no muestra Kinder ni Primaria."
        />
      ) : (
        <>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Cada generación queda en cola y la arma el worker de reportes
              (lee PostHog y la base y redacta los textos con IA); tarda uno o
              dos minutos y la lista se actualiza sola. El PDF se arma con la
              versión elegida; revísalo antes de mandarlo a la escuela.
            </Typography.Paragraph>
            <Space wrap>
              <Segmented
                options={levels}
                value={level}
                onChange={(value) => setLevel(value as AdoptionLevel)}
              />
              <DatePicker.RangePicker
                value={range}
                format="DD/MM/YYYY"
                allowClear={false}
                onChange={(value) => {
                  if (value && value[0] && value[1]) {
                    setRange([value[0], value[1]]);
                  }
                }}
              />
              <Button
                type="primary"
                loading={generation.isLoading}
                onClick={handleGenerate}
              >
                Generar nueva versión
              </Button>
            </Space>
            <Table
              rowKey="id"
              size="small"
              loading={versions.isFetching}
              columns={columns}
              dataSource={versions.data ?? []}
              pagination={false}
              locale={{ emptyText: "Todavía no hay versiones de este nivel." }}
            />
          </Space>
          <Modal
            open={quality !== null}
            onCancel={() => setQuality(null)}
            footer={null}
            width={640}
            title={
              quality ? `Calidad de la versión ${quality.report_version}` : ""
            }
          >
            {quality && <QualityDetail payload={quality} />}
          </Modal>
        </>
      )}
    </Drawer>
  );
};

function QualityDetail({ payload }: { payload: AdoptionReportPayload }) {
  const q = payload.quality;
  const aiFallbacks = q.ai.grades.filter((g) => g.source === "fallback");
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {q.unavailable.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Datos no disponibles"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {q.unavailable.map((u) => (
                <li key={u.metric}>
                  <b>{u.metric}:</b> {u.reason}
                </li>
              ))}
            </ul>
          }
        />
      ) : (
        <Alert type="success" showIcon message="Todas las métricas tienen dato." />
      )}
      <Typography.Text>
        Eventos PostHog: {q.posthog.teacher_events} de docentes y{" "}
        {q.posthog.student_events} de alumnos. Llegaron con más de 24 h de
        atraso (offline): {q.posthog.late_events}
        {q.posthog.late_pct !== null ? ` (${q.posthog.late_pct}%)` : ""}.
      </Typography.Text>
      <Typography.Text>
        Completado desde la base: {q.db_backfill.teacher_actions_added}{" "}
        acciones docentes y {q.db_backfill.student_days_added} días de alumno.
      </Typography.Text>
      <Typography.Text>
        Recursos sin semana de dosificación: {q.resources_without_week}.
        Calendario: {q.calendar_source === "SCHOOL" ? "propio de la escuela" : "general"}.
      </Typography.Text>
      {q.subjects_truncated.length > 0 && (
        <Typography.Text>
          Materias fuera del anexo por espacio: {q.subjects_truncated.join(", ")}.
        </Typography.Text>
      )}
      <Typography.Text>
        IA ({q.ai.model}, {q.ai.prompt_version}): resumen{" "}
        {q.ai.school_summary_source === "ai" ? "generado por IA" : "de respaldo"}
        {aiFallbacks.length > 0
          ? `; grados con texto de respaldo: ${aiFallbacks.map((g) => g.code).join(", ")}`
          : ""}
        .
      </Typography.Text>
      <Typography.Text type="secondary">
        {q.notes.map((note) => (
          <span key={note}>
            · {note}
            <br />
          </span>
        ))}
      </Typography.Text>
    </Space>
  );
}

export default AdoptionReportDrawer;
